import { captureException } from "@sentry/react";
import { Client, createClient } from "graphql-ws";
import useHilosStore from "src/hooks/useHilosStore";

const MAX_RETRIES = 5;
const KEEP_ALIVE_MS = 10000; // 10 seconds ping interval
const CONNECTION_ACK_TIMEOUT_MS = 10000; // 10 seconds to wait for ack
const RETRY_WAIT_BASE_MS = 1000; // Base wait time for exponential backoff

let wsClient: Client | null = null;
let retryWaitCleanup: (() => void) | null = null;

interface CreateWsClientOptions {
  url: string | (() => string | Promise<string>);
}

/**
 * Creates the GraphQL WebSocket client with reconnection and monitoring.
 *
 * Uses retryAttempts: Infinity with retryWait controlling the pacing:
 * - First MAX_RETRIES attempts: exponential backoff
 * - After MAX_RETRIES: marks as "failed" and waits for manual reconnect
 */
export function createWsClient({ url }: CreateWsClientOptions): Client {
  const { setWebSocketStatus } = useHilosStore.getState();

  setWebSocketStatus("graphql", "connecting");

  wsClient = createClient({
    url,
    connectionParams: {
      credentials: "include",
    },
    retryAttempts: Infinity,
    shouldRetry: (errOrCloseEvent) => {
      if (errOrCloseEvent instanceof CloseEvent) {
        if (errOrCloseEvent.code === 4401 || errOrCloseEvent.code === 4403) {
          console.warn("[GraphQL WS] Auth error, not retrying:", errOrCloseEvent.code);
          const { setWebSocketStatus } = useHilosStore.getState();
          setWebSocketStatus("graphql", "disconnected", "Authentication error");
          return false;
        }
        if (errOrCloseEvent.code === 1000) {
          return false;
        }
      }
      return true;
    },
    retryWait: async () => {
      const { webSocketState, incrementWebSocketRetry, setWebSocketStatus } =
        useHilosStore.getState();
      const storeRetryCount = webSocketState.graphql.retryCount;

      if (storeRetryCount < MAX_RETRIES) {
        const waitTime = Math.min(
          RETRY_WAIT_BASE_MS * Math.pow(2, storeRetryCount),
          16000
        );
        console.log(`[GraphQL WS] Retry ${storeRetryCount + 1}/${MAX_RETRIES}, waiting ${waitTime}ms`);
        incrementWebSocketRetry("graphql");
        setWebSocketStatus("graphql", "connecting");
        await new Promise((resolve) => setTimeout(resolve, waitTime));
      } else {
        console.warn("[GraphQL WS] Max retries reached, waiting for manual reconnect");
        setWebSocketStatus(
          "graphql",
          "failed",
          "Maximum reconnection attempts reached"
        );

        // Wait until retryCount resets to 0 (manual reconnect from UI)
        await new Promise<void>((resolve) => {
          const unsub = useHilosStore.subscribe((state) => {
            if (state.webSocketState.graphql.retryCount === 0) {
              unsub();
              resolve();
            }
          });
          retryWaitCleanup = () => {
            unsub();
            resolve();
          };
        });
        retryWaitCleanup = null;

        console.log("[GraphQL WS] Reconnect triggered, resuming retry loop");
        const { setWebSocketStatus: setReconnectStatus } =
          useHilosStore.getState();
        setReconnectStatus("graphql", "connecting");
      }
    },
    keepAlive: KEEP_ALIVE_MS,
    connectionAckWaitTimeout: CONNECTION_ACK_TIMEOUT_MS,
    on: {
      connected: () => {
        const { resetWebSocketRetry, setWebSocketStatus } =
          useHilosStore.getState();
        resetWebSocketRetry("graphql");
        setWebSocketStatus("graphql", "connected", null);
      },
      closed: (event) => {
        const closeEvent = event as CloseEvent;
        console.log("[GraphQL WS] Closed", closeEvent.code, closeEvent.reason);
      },
      error: (error) => {
        console.error("[GraphQL WS] Error", error);
        captureException(error);
      },
    },
  });

  return wsClient;
}

/**
 * Get the current WebSocket client instance
 */
export function getWsClient(): Client | null {
  return wsClient;
}

/**
 * Manually trigger reconnection of the GraphQL WebSocket.
 * Resets the retry count, which unblocks the parked retryWait promise.
 */
export function reconnectGraphQLWebSocket(): void {
  console.log("[GraphQL WS] Manual reconnection triggered");
  const { resetWebSocketRetry } = useHilosStore.getState();
  resetWebSocketRetry("graphql");
}

export function disposeWsClient(): void {
  if (retryWaitCleanup) {
    retryWaitCleanup();
    retryWaitCleanup = null;
  }
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
  }
}
